/**
 * What a community's pull and push compare (gh #5003, Sync rules): each synced entity's
 * normalized content, its `rev`, the `client_id` of its create, and the three-way verdict.
 *
 * Pure: no files, no requests. `sync.ts` and `courses.ts` read both sides and act on the verdicts.
 *
 * A REV is `v1:` + a short hash of a pinned canonical serialization of an entity's normalized
 * content, keyed by its kind and id. It is computed the same way from a server answer and from
 * the files, so the base an entity was last synced from travels with the files: git merges it per
 * entity, and no machine-local state exists. A rev covers an entity's own content, never its
 * place: a parent's order is compared against the list of its children's ids the files keep as of
 * the last sync (`courses.ts`).
 * A later CLI that changes a field set bumps `REV_VERSION`; a rev of another version is read as
 * no base at all, so the entity re-bases (`classify`) instead of reading as changed. The one
 * exception is a field added at its default: it joins the hashed parts only when it differs from
 * that default (`admission`, gh #5042), so every rev written before it stays the rev of the same
 * content and nothing re-bases.
 */

import { createHash } from 'node:crypto';
import { slugifyKebab, uuidV5 } from '@wayai/core';
import type {
  CommunityAdmission,
  CommunityCourseDraft,
  CommunityLessonExport,
  CommunityLessonVideo,
  CommunityModuleDraft,
  CommunityRoom,
  CommunitySettings,
} from '@wayai/core/contracts';

export const REV_VERSION = 'v1';

/** CRLF and a lone CR read as LF, on both sides. */
export function toLf(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

// ── Normalized content: the values compared, after the contracts' own transforms ──

/** Settings and `rules.md` share one rev (`settings.rev`). */
export interface SettingsContent {
  /** Trimmed, as the server stores it; '' reads as none (`communitySettingsBody`). */
  rules: string | null;
  slow_mode_seconds: number;
  courses_label: string;
  /** Who the link admits; `link` when absent, as the contract's optional field reads. */
  admission: CommunityAdmission;
}

/** The admission a community has when nothing says otherwise. */
export const DEFAULT_ADMISSION: CommunityAdmission = 'link';

export interface JoinLinkContent {
  enabled: boolean;
}

/** A room's kind is not content: it never changes once created (a read-only field). */
export interface RoomContent {
  name: string;
  archived: boolean;
}

export function normalizeRules(rules: string | null | undefined): string | null {
  if (rules === null || rules === undefined) return null;
  const trimmed = toLf(rules).trim();
  return trimmed === '' ? null : trimmed;
}

export function settingsContent(settings: {
  rules: string | null;
  slow_mode_seconds: number;
  courses_label?: string;
  admission?: CommunityAdmission;
}): SettingsContent {
  return {
    rules: normalizeRules(settings.rules),
    slow_mode_seconds: settings.slow_mode_seconds,
    courses_label: (settings.courses_label ?? '').trim(),
    admission: settings.admission ?? DEFAULT_ADMISSION,
  };
}

export function roomContent(room: { name: string; archived: boolean }): RoomContent {
  return { name: toLf(room.name).trim(), archived: room.archived };
}

/** A course's own content: not its place, nor its modules. */
export interface CourseContent {
  title: string;
  /** '' for none. */
  description: string;
  published: boolean;
}

export interface ModuleContent {
  title: string;
}

/**
 * A lesson's own content: not its module, nor its place. Its room by id (a file names it by key);
 * its video as provider and id, as `communityLessonVideoUrlSchema` reads a URL.
 */
export interface LessonContent {
  title: string;
  body: string;
  video: CommunityLessonVideo | null;
  links: Array<{ title: string; url: string }>;
  room_id: string | null;
  requires_paid_plan: boolean;
  published: boolean;
}

export function courseContent(course: { title: string; description: string; published: boolean }): CourseContent {
  return { title: toLf(course.title).trim(), description: toLf(course.description).trim(), published: course.published };
}

export function moduleContent(module: { title: string }): ModuleContent {
  return { title: toLf(module.title).trim() };
}

/** Trimmed text, the body as LF, as the lesson bodies' schemas store them. */
export function lessonContent(lesson: LessonContent): LessonContent {
  return {
    title: toLf(lesson.title).trim(),
    body: toLf(lesson.body),
    video: lesson.video ? { provider: lesson.video.provider, id: lesson.video.id } : null,
    links: lesson.links.map((link) => ({ title: toLf(link.title).trim(), url: link.url.trim() })),
    room_id: lesson.room_id,
    requires_paid_plan: lesson.requires_paid_plan,
    published: lesson.published,
  };
}

// ── Revs ──

type RevPart = string | number | boolean | null | RevPart[];

function rev(parts: RevPart[]): string {
  return `${REV_VERSION}:${createHash('sha256').update(JSON.stringify(parts)).digest('hex').slice(0, 12)}`;
}

/** `admission` is hashed only when it is not the default: see the header. */
export const settingsRev = (communityId: string, c: SettingsContent): string =>
  rev([
    'settings', communityId, c.rules, c.slow_mode_seconds, c.courses_label,
    ...(c.admission === DEFAULT_ADMISSION ? [] : [c.admission]),
  ]);

export const joinLinkRev = (communityId: string, c: JoinLinkContent): string =>
  rev(['join_link', communityId, c.enabled]);

export const roomRev = (roomId: string, c: RoomContent): string => rev(['room', roomId, c.name, c.archived]);

/** A server answer's settings, as content and rev. */
export function serverSettings(communityId: string, settings: CommunitySettings): { content: SettingsContent; rev: string } {
  const content = settingsContent(settings);
  return { content, rev: settingsRev(communityId, content) };
}

export function serverRoom(room: CommunityRoom): { content: RoomContent; rev: string } {
  const content = roomContent(room);
  return { content, rev: roomRev(room.room_id, content) };
}

export const courseRev = (courseId: string, c: CourseContent): string => rev(['course', courseId, c.title, c.description, c.published]);

export const moduleRev = (moduleId: string, c: ModuleContent): string => rev(['module', moduleId, c.title]);

export const lessonRev = (lessonId: string, c: LessonContent): string =>
  rev([
    'lesson', lessonId, c.title, c.body, c.video?.provider ?? null, c.video?.id ?? null,
    c.links.map((link) => [link.title, link.url]), c.room_id, c.requires_paid_plan, c.published,
  ]);

export function serverCourse(course: CommunityCourseDraft): { content: CourseContent; rev: string } {
  const content = courseContent(course);
  return { content, rev: courseRev(course.course_id, content) };
}

export function serverModule(module: CommunityModuleDraft): { content: ModuleContent; rev: string } {
  const content = moduleContent(module);
  return { content, rev: moduleRev(module.module_id, content) };
}

/** A lesson as the server answers it (the export, or a create's or an update's answer). */
export type ServerLesson = Pick<CommunityLessonExport, 'lesson_id' | 'module_id' | 'title' | 'video' | 'body' | 'links' | 'room_id' | 'requires_paid_plan' | 'published' | 'position'>;

export function serverLesson(lesson: ServerLesson): { content: LessonContent; rev: string } {
  const content = lessonContent(lesson);
  return { content, rev: lessonRev(lesson.lesson_id, content) };
}

// ── The three-way verdict (the Sync rules table) ──

/**
 * - `in_sync`   — files and server both still at the base: nothing to do.
 * - `local`     — the files changed: push writes them; pull keeps them (an unpushed edit).
 * - `remote`    — the server changed: pull takes it; push leaves it ("pull to take it").
 * - `converged` — both changed alike (or no base, but they agree): refresh the rev, write nothing.
 * - `conflict`  — both changed, differently (or no base, and they differ): push refuses the
 *                 entity; pull reports it, and `--theirs` takes the server's.
 */
export type SyncState = 'in_sync' | 'local' | 'remote' | 'converged' | 'conflict';

/** A base this CLI reads: a rev of its own version; anything else is none. */
export function knownBase(base: string | undefined): string | undefined {
  return base !== undefined && base.startsWith(`${REV_VERSION}:`) ? base : undefined;
}

export function classify(base: string | undefined, file: string, server: string): SyncState {
  const known = knownBase(base);
  if (known === undefined) return file === server ? 'converged' : 'conflict';
  return threeWay(file !== known, server !== known, file === server);
}

/** The Sync rules table: which sides changed since the base, and whether they now agree. */
export function threeWay(fileChanged: boolean, serverChanged: boolean, agree: boolean): SyncState {
  if (!fileChanged && !serverChanged) return 'in_sync';
  if (!serverChanged) return 'local';
  if (!fileChanged) return 'remote';
  return agree ? 'converged' : 'conflict';
}

// ── Creates: the stable local name → `client_id` (Decision 9) ──

export type CreateKind = 'room' | 'course' | 'module' | 'lesson';

/**
 * The `client_id` of a create: a UUIDv5 (RFC 9562 §5.5) in the community's id as namespace, of
 * the entity's kind and its stable local name. Deterministic, so a create retried after a lost
 * response — or after the files lost an id the push wrote — answers the entity made the first
 * time instead of making a second one.
 */
export function createClientId(communityId: string, kind: CreateKind, localName: string): Promise<string> {
  return uuidV5(communityId, `${kind}:${localName}`);
}

/**
 * A stable local name for an entity first written by pull: its name's slug, or `fallback` when
 * the name has none; one already taken becomes `<slug>--<id-prefix>` (the Sync rules).
 */
export function localName(name: string, id: string, taken: ReadonlySet<string>, fallback: string): string {
  const slug = slugifyKebab(name) || fallback;
  if (!taken.has(slug)) return slug;
  const withPrefix = `${slug}--${id.slice(0, 8)}`;
  return taken.has(withPrefix) ? `${slug}--${id}` : withPrefix;
}
